/**
 * Higher priority dialogs show first
 */
export const NOTIFICATION_DIALOG_PRIORITY = Object.freeze({
  HIGHEST: 10,
  HIGH: 7,
  MEDIUM: 5,
  LOW: 3,
  LOWEST: 1,
});

export const NOTIFICATION_DIALOG_MESSAGES = Object.freeze({
  TRANSACTION_GENERIC_ERROR: {
    title: 'Unable to process transaction',
    message:
      'An issue occurred while trying to process your transaction. Please try again.',
    priority: NOTIFICATION_DIALOG_PRIORITY.LOWEST,
  },
  PROHIBITED_ITEMS: {
    title: 'Prohibited Item',
    message: 'Item sale is prohibited.',
    priority: NOTIFICATION_DIALOG_PRIORITY.LOWEST,
  },
  REIMBURSABLE_MEAL_PURCHASED: {
    title: 'Already Purchased',
    message:
      'Student has already purchased a reimbursable meal. Please select a non-reimbursable item.',
    priority: NOTIFICATION_DIALOG_PRIORITY.LOWEST,
  },
  REIMBURSABLE_MEAL_MULTIPLE_PURCHASE: {
    title: 'Transaction cannot by processed.',
    message:
      'Student may only purchase one reimbursable meal per Meal Type. Please select a non-reimbursable item to proceed.',
    priority: NOTIFICATION_DIALOG_PRIORITY.LOWEST,
  },
  REIMBURSABLE_SNACK_PURCHASED: {
    title: 'Already Purchased',
    message:
      'Student has already purchased a reimbursable snack. Please select a non-reimbursable item.',
    priority: NOTIFICATION_DIALOG_PRIORITY.LOWEST,
  },
  REIMBURSABLE_SNACK_MULTIPLE_PURCHASE: {
    title: 'Transaction cannot by processed.',
    message:
      'Students may only purchase one reimbursable snack per Meal Type. Please select a non-reimbursable item to proceed.',
    priority: NOTIFICATION_DIALOG_PRIORITY.LOWEST,
  },
  CHARGED_MEAL_COUNT_LIMIT: {
    title: 'Meal Count Limit Reached',
    message:
      'Reimbursable item cannot be added. Individual has reached the set meal count limit.',
    priority: NOTIFICATION_DIALOG_PRIORITY.LOWEST,
  },
  CHARGED_MEAL_AMOUNT_LIMIT: {
    title: 'Meal Amount Limit Reached',
    message:
      'Reimbursable item cannot be added. Individual has reached the set meal amount limit.',
    priority: NOTIFICATION_DIALOG_PRIORITY.LOWEST,
  },
  STUDENT_HAS_ALLERGENS: {
    title: 'Student has allergens',
    priority: NOTIFICATION_DIALOG_PRIORITY.HIGHEST,
  },
  SPECIAL_MESSAGE: {
    title: 'Special Message',
    priority: NOTIFICATION_DIALOG_PRIORITY.HIGH,
  },
  INVALID_STUDENT: {
    title: 'Invalid Student',
    message: 'An invalid student was entered via the keypad.',
    priority: NOTIFICATION_DIALOG_PRIORITY.HIGHEST,
  },
  TEACHER_STUDENT_NOT_ALLOWED: {
    title: 'Error',
    message: 'Student is not allowed to purchase the quick serve item.',
    priority: NOTIFICATION_DIALOG_PRIORITY.LOWEST,
  },
  TEACHER_STUDENTS_NOT_ALLOWED: {
    title: 'Attention',
    message:
      'Some students are not selected because they are not allowed to purchase the quick serve item.',
    priority: NOTIFICATION_DIALOG_PRIORITY.LOWEST,
  },
  GENERIC_ACCOUNTS_CASH_ONLY: {
    title: 'Error',
    message:
      'Only Cash and Check transactions are allowed for generic accounts',
    priority: NOTIFICATION_DIALOG_PRIORITY.HIGHEST,
  },
  WITHDRAWN_STUDENT_SERVE_ATTEMPT: {
    title: 'Error',
    message: `Withdrawn students cannot be served items.`,
    priority: NOTIFICATION_DIALOG_PRIORITY.HIGHEST,
  },
  CYCLE_MENU_WILL_OVERWRITE: {
    title: 'Warning',
    message:
      'One or more Applied Menus already exist for the Site(s) and Date(s) selected. Would you like to continue and update all Applied Menus that have not been modified in Production?',
    priority: NOTIFICATION_DIALOG_PRIORITY.MEDIUM,
  },
  CYCLE_MENU_HAS_PRODUCTION_RECORDS: {
    title: 'Warning',
    message:
      'Applied Menus cannot be updated, one or more Production Records have been modified for the Site(s) and Date(s) selected.',
    priority: NOTIFICATION_DIALOG_PRIORITY.MEDIUM,
  },
  CYCLE_MENU_HAS_CLOSED_PRODUCTION_RECORDS: {
    title: 'Error',
    message:
      'Applied Menus cannot be updated, one or more Production Records have been closed for the Site(s) and Date(s) selected.',
    priority: NOTIFICATION_DIALOG_PRIORITY.MEDIUM,
  },
  APPLIED_MENU_HAS_PRODUCTION_RECORDS: {
    title: 'Warning',
    message:
      'One or more Production Records have been modified for the Date(s) selected. Are you sure you want to update this applied menu?',
    priority: NOTIFICATION_DIALOG_PRIORITY.MEDIUM,
    okButtonText: 'Yes',
    showCancel: true,
  },
  UNABLE_TO_PUBLISH_POS_MISSING_OVS: {
    title: 'This menu cannot be published to Point Of Sale.',
    showOkButtonIcon: false,
  },
  UNABLE_TO_PUBLISH_POS_MISSING_OVS_AND_DIGITIAL_MENU_SUITE: {
    title:
      'This menu cannot be published to Point Of Sale and was not published to the Digital Menu Suite.',
    showOkButtonIcon: false,
  },
  DELETE_MENU_ITEM: {
    title: 'Warning',
    message: 'Are you sure you want to delete this item from the menu?',
    priority: NOTIFICATION_DIALOG_PRIORITY.MEDIUM,
    showCancel: true,
    okButtonText: 'Yes',
  },
  DELETE_MENU_ITEMS: {
    title: 'Warning',
    message:
      'Are you sure you want to delete the selected item(s) from the menu?',
    priority: NOTIFICATION_DIALOG_PRIORITY.MEDIUM,
    showCancel: true,
    okButtonText: 'Yes',
  },
  DELETE_MULTIPLE_MENU_ITEMS: {
    title: 'Warning',
    message: 'Are you sure you want to delete all the items in this category?',
    priority: NOTIFICATION_DIALOG_PRIORITY.MEDIUM,
    showCancel: true,
    okButtonText: 'Yes',
  },
  DETACH_RECIPE: {
    title: 'Warning',
    message: 'Are you sure you want to detach this item?',
    priority: NOTIFICATION_DIALOG_PRIORITY.MEDIUM,
    showCancel: true,
    okButtonText: 'Yes',
  },
  RECIPE_INACTIVE: {
    title: 'Set To Inactive?',
    message: 'Are you sure you want to set the recipe to inactive?',
    priority: NOTIFICATION_DIALOG_PRIORITY.MEDIUM,
    showCancel: true,
    okButtonText: 'Yes',
  },
  RECIPE_ACTIVE: {
    title: 'Set To Active?',
    message: 'Are you sure you want to set the recipe to active?',
    priority: NOTIFICATION_DIALOG_PRIORITY.MEDIUM,
    showCancel: true,
    okButtonText: 'Yes',
  },
  ALLERGEN_FROM_INGREDIENT: {
    title: 'Allergen Derived From Ingredient',
    message:
      'This setting cannot be modified because this allergen is derived from an ingredient.',
    priority: NOTIFICATION_DIALOG_PRIORITY.MEDIUM,
    showCancel: false,
    okButtonText: 'Ok',
  },
  ALLERGENS_FROM_INGREDIENTS: {
    title: 'Allergens Derived From Ingredients',
    message:
      'This setting cannot be modified because some allergens are derived from some ingredients.',
    priority: NOTIFICATION_DIALOG_PRIORITY.MEDIUM,
    showCancel: false,
    okButtonText: 'Ok',
  },
  DELETE_INGREDIENT_FROM_RECIPE: {
    title: 'Warning',
    message:
      'Are you sure you want to delete this ingredient from this recipe?',
    priority: NOTIFICATION_DIALOG_PRIORITY.MEDIUM,
    showCancel: true,
    okButtonText: 'Yes',
  },
  INGREDIENT_INACTIVE: {
    title: 'Set To Inactive?',
    message: 'Are you sure you want to set the ingredient to inactive?',
    priority: NOTIFICATION_DIALOG_PRIORITY.MEDIUM,
    showCancel: true,
    okButtonText: 'Yes',
  },
  INGREDIENT_ACTIVE: {
    title: 'Set To Active?',
    message: 'Are you sure you want to set the ingredient to active?',
    priority: NOTIFICATION_DIALOG_PRIORITY.MEDIUM,
    showCancel: true,
    okButtonText: 'Yes',
  },
  CLOSE_SCHOOL_YEAR_WARNING: {
    title: 'Warning',
    message:
      'If other users are editing data when you run Close School Year, you may experience unexpected data changes.\n' +
      'Please ensure that you are the only user currently logged in.\n\n' +
      'It is recommended that you perform Close School Year during non-business hours.',
    okButtonText: 'I understand, and I am currently the only user logged in.',
  },
  ATTRIBUTE_FROM_INGREDIENT: {
    title: 'Attribute Derived From Ingredient',
    message:
      'This setting cannot be modified because this attribute is derived from an ingredient.',
    priority: NOTIFICATION_DIALOG_PRIORITY.MEDIUM,
    showCancel: false,
    okButtonText: 'Ok',
  },
  SAVE_BEFORE_PROCEEDING: {
    title: 'Save Changes?',
    message: 'Would you like to save your changes before proceeding?',
    priority: NOTIFICATION_DIALOG_PRIORITY.MEDIUM,
    showCancel: true,
    okButtonText: 'Ok',
  },
  MENU_UNLINK_INVENTORY_ITEM: {
    title: 'Unlink Inventory Item',
    message: 'Are you sure you want to unlink the inventory item?',
    priority: NOTIFICATION_DIALOG_PRIORITY.MEDIUM,
    showCancel: true,
    okButtonText: 'Yes',
  },
  MENU_RESET_PURCHASING_UNIT_FORM: {
    title: 'Reset manual entry',
    message: 'Are you sure you want to reset the manually entered fields?',
    priority: NOTIFICATION_DIALOG_PRIORITY.MEDIUM,
    showCancel: true,
    okButtonText: 'Yes',
  },
  INGREDIENT_USED_ON_RECIPES: {
    title: 'Cannot delete ingredient',
    message: '',
    priority: NOTIFICATION_DIALOG_PRIORITY.MEDIUM,
    showCancel: false,
    okButtonText: 'Ok',
  },
  DELETE_INGREDIENT: {
    title: 'Warning',
    message: 'Are you sure you want to delete this ingredient?',
    priority: NOTIFICATION_DIALOG_PRIORITY.MEDIUM,
    showCancel: true,
    okButtonText: 'Yes',
  },
  DUPLICATE_INGREDIENT: {
    title: 'Duplicate Ingredient',
    message: 'Are you sure you want to duplicate this ingredient?',
    priority: NOTIFICATION_DIALOG_PRIORITY.MEDIUM,
    showCancel: true,
    okButtonText: 'Yes',
  },
  DELETE_INGREDIENT_MEASURE: {
    title: 'Warning',
    message: 'Are you sure you want to delete this ingredient measure?',
    priority: NOTIFICATION_DIALOG_PRIORITY.MEDIUM,
    showCancel: true,
    okButtonText: 'Yes',
  },
  CONVERT_INGREDIENT: {
    title: 'Warning',
    message: 'Are you sure you want to convert this ingredient into a recipe?',
    priority: NOTIFICATION_DIALOG_PRIORITY.MEDIUM,
    showCancel: true,
    okButtonText: 'Yes',
  },
  CONVERT_RECIPE: {
    title: 'Warning',
    message: 'Are you sure you want to convert this recipe into an ingredient?',
    priority: NOTIFICATION_DIALOG_PRIORITY.MEDIUM,
    showCancel: true,
    okButtonText: 'Yes',
  },
  REMOVE_TYPE_OF_FAT: {
    title: 'Warning',
    message: 'Are you sure you want to remove the type of fat?',
    priority: NOTIFICATION_DIALOG_PRIORITY.MEDIUM,
    showCancel: true,
    okButtonText: 'Yes',
  },
  BULK_UPDATE_BID_DETAIL_ITEM_UPDATE: {
    title: 'Inventory',
    message: 'You must check an item to update.',
    priority: NOTIFICATION_DIALOG_PRIORITY.MEDIUM,
    showCancel: false,
    okButtonText: 'OK',
  },
  BULK_UPDATE_BID_DETAIL_DATE_VALIDATION: {
    title: 'Bid Price Date Validation',
    message:
      'Bid Price Begin Date must be less than or equal to Bid Price End Date.',
    priority: NOTIFICATION_DIALOG_PRIORITY.MEDIUM,
    showCancel: false,
    okButtonText: 'OK',
  },
  REMOVE_HACCP: {
    title: 'Warning',
    message: 'Are you sure you want to delete this HACCP?',
    priority: NOTIFICATION_DIALOG_PRIORITY.MEDIUM,
    showCancel: true,
    okButtonText: 'Yes',
  },
  REMOVE_INGREDIENT: {
    title: 'Warning',
    message: 'Are you sure you want to remove this ingredient?',
    priority: NOTIFICATION_DIALOG_PRIORITY.MEDIUM,
    showCancel: true,
    okButtonText: 'Yes',
  },
  REMOVE_HACCP_TYPE: {
    title: 'Warning',
    message: 'Are you sure you want to delete this HACCP Type?',
    priority: NOTIFICATION_DIALOG_PRIORITY.MEDIUM,
    showCancel: true,
    okButtonText: 'Yes',
  },
  REMOVE_HACCP_DIRECTION: {
    title: 'Warning',
    message: 'Are you sure you want to remove this HACCP direction?',
    priority: NOTIFICATION_DIALOG_PRIORITY.MEDIUM,
    showCancel: true,
    okButtonText: 'Yes',
  },
  REMOVE_PREPARATION_STEP: {
    title: 'Warning',
    message: 'Are you sure you want to remove this step?',
    priority: NOTIFICATION_DIALOG_PRIORITY.MEDIUM,
    showCancel: true,
    okButtonText: 'Yes',
  },
  REMOVE_RECIPE_GROUP: {
    title: 'Warning',
    message: 'Are you sure you want to remove this Recipe Group?',
    priority: NOTIFICATION_DIALOG_PRIORITY.MEDIUM,
    showCancel: true,
    okButtonText: 'Yes',
  },
  REMOVE_INGREDIENT_GROUP: {
    title: 'Warning',
    message: 'Are you sure you want to remove this Ingredient Group?',
    priority: NOTIFICATION_DIALOG_PRIORITY.MEDIUM,
    showCancel: true,
    okButtonText: 'Yes',
  },
  KITCHEN_PREP_CLOSE_FULFILLMENT_VALIDATION: {
    title: 'Kitchen Prep Worksheet',
    message: 'Please only select open Kitchen Prep Worksheet to close.',
    priority: NOTIFICATION_DIALOG_PRIORITY.MEDIUM,
    showCancel: false,
    okButtonText: 'OK',
  },
  CREATE_EDIT_TRUCK_ROUTE_SITE_ITEMS_VALIDATION: {
    title: 'Truck Route',
    message: '',
    priority: NOTIFICATION_DIALOG_PRIORITY.MEDIUM,
    showCancel: false,
    okButtonText: 'OK',
  },
  PROCESS_MESSAGE_ERROR: {
    title: 'Inventory',
    message: '',
    priority: NOTIFICATION_DIALOG_PRIORITY.MEDIUM,
    showCancel: false,
    okButtonText: 'OK',
  },
  ZERO_NEGATIVE_BALANVE_NO_DATA_MESSAGE: {
    title: 'No Accounts Found',
    message: 'No accounts with negative balances were found',
    priority: NOTIFICATION_DIALOG_PRIORITY.MEDIUM,
    showCancel: false,
    okButtonText: 'OK',
  },
  EDIT_RECIPE_VIEW_PREP_FORM_DIRTY: {
    title: 'Un-saved changes detected',
    message:
      'You must save your changes before you can view the Recipe Preparation Report.',
    priority: NOTIFICATION_DIALOG_PRIORITY.MEDIUM,
    showCancel: true,
    cancelButtonText: 'No, return to edited page.',
    okButtonText: 'Yes, save my changes',
  },
  PROVISION2_PROCESS_MEALS: {
    title: 'Please verify information before processing transactions.',
    priority: NOTIFICATION_DIALOG_PRIORITY.MEDIUM,
    showCancel: true,
    okButtonText: 'Process',
  },
  CONTROL_INFORMATION_DISTRICT_SETTING_MOBILE_SYNC_INFO: {
    title: 'Sync Mobile Info',
    message:
      'Sync Mobile Info will clear any data that has not been submitted from the Mobile Inventory.\n' +
      'Please log out and log back into the Mobile Inventory on your device to view new information.\n\n' +
      'Are you sure you would like to continue?',
    priority: NOTIFICATION_DIALOG_PRIORITY.MEDIUM,
    showCancel: true,
    okButtonText: 'OK',
  },
  TRUCK_ROUTE_REPORT_SITE_SELECTION_VALIDATION: {
    title: 'Truck Route Report',
    message: 'You must select at least 1 site.',
    priority: NOTIFICATION_DIALOG_PRIORITY.MEDIUM,
    showCancel: false,
    okButtonText: 'OK',
  },
  APPLICATION_HUB_REPROCESS_MEALS: {
    title: 'Reprocess Meals',
    message:
      'Reprocess Meals for the selected student(s) who have been approved for free or reduced meals, but already had some meal transactions entered at a different status, from the application date to today’s date?' +
      '\n\nThis will allow the district to claim the meals at the correct reimbursement rate and remove any accumulated charges.',
    priority: NOTIFICATION_DIALOG_PRIORITY.MEDIUM,
    showCancel: true,
    okButtonText: 'Reprocess',
  },
  API_INACCESSIBLE: {
    type: 'API_INACCESSIBLE',
    title: 'Waiting For Connection',
    message:
      'The web service is currently inaccessible. If you would like to continue in Offline Mode, click Go to Home.',
    priority: NOTIFICATION_DIALOG_PRIORITY.MEDIUM,
    showCancel: false,
    okButtonText: 'Go to Home',
  },
  API_INACCESSIBLE_LOGIN: {
    type: 'API_INACCESSIBLE',
    title: 'Continue In Offline Mode?',
    message:
      'The web service is inaccessible, would you like to continue in offline mode?',
    priority: NOTIFICATION_DIALOG_PRIORITY.MEDIUM,
    showCancel: true,
    okButtonText: 'Continue In Offline Mode',
  },
  PRODUCTION_RECORD_DELETE_RECIPE: {
    title: 'Warning',
    message:
      'Are you sure you want to remove this recipe from the production record?',
    showCancel: true,
  },
  DAILY_DEPOSIT_USING_CASH_CALCULATOR: {
    title: 'Warning',
    message:
      'A manual deposit can only be entered if the Cash Calculator is closed and cleared of all values.',
    showCancel: false,
    priority: NOTIFICATION_DIALOG_PRIORITY.MEDIUM,
  },
  PRODUCTION_RECORD_START_OVER: {
    title: 'Start Over',
    message:
      'All production data will be deleted and the record will be reset to a PLANNING status.\nAre you sure you want to start over?',
    showCancel: true,
    priority: NOTIFICATION_DIALOG_PRIORITY.MEDIUM,
    okButtonText: 'Yes',
  },
  DELETE_RECIPE_SCENARIO_1: {
    title: 'Unable to delete Recipe',
    message:
      'Recipe is a sub-recipe (converted to an Ingredient) and cannot be deleted until the sub-recipe is deleted.',
    showCancel: false,
    priority: NOTIFICATION_DIALOG_PRIORITY.MEDIUM,
  },
  DELETE_RECIPE_SCENARIO_2: {
    title: 'Unable to delete Recipe',
    message: '',
    showCancel: true,
    priority: NOTIFICATION_DIALOG_PRIORITY.MEDIUM,
    okButtonText: 'Yes',
  },
  DELETE_RECIPE_CONFIRM: {
    title: 'Delete Recipe',
    message: 'Are you sure you want to delete this recipe?',
    showCancel: true,
    priority: NOTIFICATION_DIALOG_PRIORITY.MEDIUM,
    okButtonText: 'Yes',
  },
  DUPLICATE_RECIPE_CONFIRM: {
    title: 'Duplicate Recipe',
    message: 'Are you sure you want to duplicate this recipe?',
    showCancel: true,
    priority: NOTIFICATION_DIALOG_PRIORITY.MEDIUM,
    okButtonText: 'Yes',
  },
  NETWORKFILTERING_IPNOTVALID: {
    title: 'Unable to Enable Network Filtering',
    message:
      'Current IP is not valid. Please add your IP to the list and try again.',
    priority: NOTIFICATION_DIALOG_PRIORITY.MEDIUM,
  },
  HOME_CONFIRM_SERVE_MEAL_COUNTER: {
    // Notification dialog for Serve/Home button pressed, Meal Counter
    title: 'CEP meals have not been processed.',
    message:
      'Exiting will clear the served meal count.  Are you sure you want to leave Point of Sale?',
    showCancel: true,
    okButtonText: 'Yes',
    cancelButtonText: 'No',
  },
  HOME_CONFIRM_SERVE_INCOMPLETE_TRANSACTION: {
    // Notification Dialog for Serve/Home button pressed with incomplete transaction
    title: 'Previous Transaction is Incomplete.',
    message:
      'The previous transaction has not been processed.  Please Process or Clear the transaction to exit the Serve screen.',
    showCancel: false,
    okButtonText: 'OK',
  },
  HOME_CONFIRM_SERVE: {
    // Notification dialog for Serve/Home button pressed
    title: 'Go back to home screen',
    message: 'Are you sure you want to leave Point of Sale?',
    showCancel: true,
    okButtonText: 'Yes',
    cancelButtonText: 'No',
  },
  ALACARTE_EXCEEDED: {
    title: 'Warning',
    message: 'Account has exceeded a la carte limit.',
    priority: NOTIFICATION_DIALOG_PRIORITY.LOWEST,
  },
  DELETE_APPLIED_MENU_CONFIRM: {
    title: 'Delete Applied Menu',
    message: 'Are you sure you want to delete this applied menu?',
    showCancel: true,
    priority: NOTIFICATION_DIALOG_PRIORITY.MEDIUM,
    okButtonText: 'Yes',
  },
  DIRTY_PROMPT: {
    title: 'Un-saved changes detected',
    message:
      'Are you sure you want to close this window and lose any unsaved changes?',
    showCancel: true,
    priority: NOTIFICATION_DIALOG_PRIORITY.MEDIUM,
    cancelButtonText: 'No, return to edited page.',
    okButtonText: 'Yes, discard changes.',
  },
  DC_UNMATCHED_DECLINED_BENEFITS: {
    title: 'Delete Unmatched DC Imported Student',
    message:
      'The selected student is in a household that has Declined Benefits. Are you sure you want to delete Unmatched DC Imported Student?',
    priority: NOTIFICATION_DIALOG_PRIORITY.LOWEST,
    showCancel: true,
    okButtonText: 'OK',
  },
  REMOVE_FOUND_RECIPES: {
    title: 'Remove Found Recipes',
    message: 'Are you sure you want to remove the recipe(s)?',
    showCancel: true,
    priority: NOTIFICATION_DIALOG_PRIORITY.MEDIUM,
  },
  REPLACE_FOUND_RECIPES: {
    title: 'Replace Found Recipes',
    message: 'Are you sure you want to replace the recipe(s)?',
    showCancel: true,
    priority: NOTIFICATION_DIALOG_PRIORITY.MEDIUM,
  },
  CANNOT_REPLACE_RECIPES: {
    title: 'Cannot replace recipes',
    message:
      'The following entree recipe(s) cannot be replaced with a non-entree recipe due to other recipe(s) being served as part of them:\n',
    showCancel: false,
    priority: NOTIFICATION_DIALOG_PRIORITY.MEDIUM,
  },
  CANNOT_REPLACE_RECIPES_DUPLICATES: {
    title: 'Cannot replace recipes',
    message:
      'The following days would have duplicate recipes from replacing:\n',
  },
  CONFIRM_UPDATE_EXISTING_APP: {
    title: 'Update Existing Application',
    message: 'Are you sure you want to update the existing application?',
    priority: NOTIFICATION_DIALOG_PRIORITY.MEDIUM,
    showCancel: true,
    cancelButtonText: 'No',
    okButtonText: 'Yes',
  },
  CONFIRM_CREATE_NEW_APP: {
    title: 'Create New Application',
    message: 'Are you sure you want to start a new application?',
    priority: NOTIFICATION_DIALOG_PRIORITY.MEDIUM,
    showCancel: true,
    cancelButtonText: 'No',
    okButtonText: 'Yes',
  },
  REOPEN_PRODUCT_RECORD_CONFIRM: {
    title: 'Warning',
    message:
      'You are about to open a closed production record. Select Confirm below to continue with reopening or Cancel to go back to previous page.',
    showCancel: true,
    priority: NOTIFICATION_DIALOG_PRIORITY.MEDIUM,
    okButtonText: 'Confirm',
    cancelButtonText: 'Cancel',
  },
  REMOVE_INGREDIENTS_FROM_RECIPES: {
    title: 'Warning',
    message:
      'Are you sure you want to remove this ingredient from the recipe(s)?',
    showCancel: true,
    priority: NOTIFICATION_DIALOG_PRIORITY.MEDIUM,
    okButtonText: 'Yes',
  },
  REPLACE_INGREDIENTS_ON_RECIPES: {
    title: 'Warning',
    message:
      'Are you sure you want to replace this ingredient in the recipe(s)?',
    showCancel: true,
    priority: NOTIFICATION_DIALOG_PRIORITY.MEDIUM,
    okButtonText: 'Yes',
  },
});



// WEBPACK FOOTER //
// ./src/common/Constants/dialog.js